// routes/ProtectedRoute.jsx
import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useSelector,useDispatch } from 'react-redux';
import { adminApi } from '../services/adminApi';import { usePlatformSettings } from '../hooks/usePlatformSettings';
import { apiService } from '../services/api';
import { safeSessionStorage } from '../utils/safeStorage';
import AccountBanned, { ACCOUNT_BLOCKED_EVENT } from '../components/common/AccountBanned';

import { 
 setAdminStatus
} from '../features/auth/authSlice';
// Props:
// - children: JSX
// - requireAdmin?: boolean
const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const { isAuthenticated, isHydrated, isAdmin, needsProfileSetup, profile } = useSelector((state) => state.auth);const { settings, loadingSettings } = usePlatformSettings();

const dispatch = useDispatch();
  const [adminState, setAdminState] = useState({
    checked: !requireAdmin, // if not admin route, no need to check
    isAdmin: !requireAdmin,
  });
  // Set when any API call comes back 403 `account_banned` (see api.js), for a
  // creator banned after their profile was loaded into the store.
  const [bannedMidSession, setBannedMidSession] = useState(false);

  useEffect(() => {
    const onBlocked = (e) => { if (e.detail === 'banned') setBannedMidSession(true); };
    window.addEventListener(ACCOUNT_BLOCKED_EVENT, onBlocked);
    return () => window.removeEventListener(ACCOUNT_BLOCKED_EVENT, onBlocked);
  }, []);

 // Fire once per browser session to record last-login IP + location on the creator record.
  useEffect(() => {
    if (!isAuthenticated || !isHydrated || safeSessionStorage.getItem('ml_hb')) return;
    safeSessionStorage.setItem('ml_hb', '1');
    apiService.heartbeat();
  }, [isAuthenticated, isHydrated]);

  useEffect(() => {
    if (!requireAdmin || !isHydrated || !isAuthenticated) return;

    let cancelled = false;

    const checkAdmin = async () => {
      try {
        // Base admin gate — any role (or env break-glass uid) resolves here,
        // regardless of which specific module permissions they hold.
        await adminApi.getMyAccess();
        if (!cancelled) {
          setAdminState({ checked: true, isAdmin: true });
          dispatch(setAdminStatus(true));  // 👈 SYNC TO REDUX
        }
      } catch (err) {
        if (!cancelled) {
          setAdminState({ checked: true, isAdmin: false });
          dispatch(setAdminStatus(false));  // 👈 SYNC TO REDUX
        }
      }
    };

    checkAdmin();
    return () => { cancelled = true; };
  }, [requireAdmin, isHydrated, isAuthenticated, dispatch]);
  // Global auth hydration spinner
  if (!isHydrated) {
    return (
      <div className="bg-[#F4FAF6] min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#32764A] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Not logged in
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Signed in but no creator profile yet: someone who signed in with Google
  // and left before finishing setup, then came back. The auth listener marks
  // them authenticated, so without this every creator page opened for them,
  // and anything that needs the profile failed. Verifying a mobile number on
  // /profile, for instance, came back "Creator profile not found". /login
  // renders AuthFlow, which shows the setup step for exactly this state.
  // `!profile` matters: completeProfileSetup leaves needsProfileSetup true
  // through onboarding steps 2-3 but sets profile, and "Go to dashboard" at
  // the end must not bounce a brand-new creator back to setup.
  if (needsProfileSetup && !profile) {
    return <Navigate to="/login" replace />;
  }

  // Banned by an admin: nothing but the banned screen. Admin routes are left
  // alone — staff are never banned creators, and adminApi gates those anyway.
  if (!requireAdmin && (profile?.status === 'banned' || bannedMidSession)) {
    return <AccountBanned />;
  }
if (settings?.maintenance_mode && !requireAdmin && !adminState.checked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 to-orange-50 p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">🔧 Maintenance Mode</h2>
          <p className="text-gray-600 mb-6">
            We're currently performing maintenance to improve your experience. 
            We'll be back shortly!
          </p>
          <div className="bg-amber-50 rounded-lg p-4 text-sm text-amber-900">
            <p className="font-semibold mb-1">What's happening?</p>
            <p>Our platform is being updated. Please check back in a few minutes.</p>
          </div>
        </div>
      </div>
    );
  }
  // Admin-only route: wait for admin check
  if (requireAdmin && !adminState.checked) {
    return (
      <div className="bg-[#F4FAF6] min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#32764A] mx-auto mb-4"></div>
          <p className="text-gray-600">Checking admin access...</p>
        </div>
      </div>
    );
  }

  // Admin-only route & user is not admin → redirect to normal dashboard
  if (requireAdmin && !adminState.isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

export default ProtectedRoute;
